import { FilterState } from "@/components/ProjectList/types";

export const getDefaultFilters = (): FilterState => ({
  userIds: [],
  labels: [],
  search: "",
  statuses: [],
  estimates: [],
  priorities: [],
  dueDates: {
    none: false,
    overdue: false,
    today: false,
    thisWeek: false,
    nextWeek: false,
    later: false,
  },
});

export const hasActiveFilters = (filters: FilterState | undefined): boolean => {
  if (!filters) return false;

  return (
    (filters.search?.length ?? 0) > 0 ||
    (filters.userIds?.length ?? 0) > 0 ||
    (filters.labels?.length ?? 0) > 0 ||
    (filters.statuses?.length ?? 0) > 0 ||
    (filters.estimates?.length ?? 0) > 0 ||
    (filters.priorities?.length ?? 0) > 0 ||
    (filters.dueDates && Object.values(filters.dueDates).some(Boolean))
  );
};

const isDefaultCondition = (value: string): boolean => {
  return value === "is" || value === "is any of";
};

export const serializeFiltersToParams = (
  params: URLSearchParams,
  filters: FilterState | undefined,
): URLSearchParams => {
  const newParams = new URLSearchParams(params);

  // Clear all filter params
  const filterKeys = [
    "filter_userIds",
    "filter_labels",
    "filter_search",
    "filter_statuses",
    "filter_estimates",
    "filter_priorities",
    "filter_dueDates",
    "filter_conditions",
  ];
  filterKeys.forEach((key) => newParams.delete(key));

  if (!hasActiveFilters(filters)) {
    return newParams;
  }

  if (!filters) return newParams;

  if (filters.userIds?.length) {
    newParams.set("filter_userIds", filters.userIds.join(","));
  }
  if (filters.labels?.length) {
    newParams.set("filter_labels", filters.labels.join(","));
  }
  if (filters.statuses?.length) {
    newParams.set("filter_statuses", filters.statuses.join(","));
  }
  if (filters.estimates?.length) {
    newParams.set("filter_estimates", filters.estimates.join(","));
  }
  if (filters.priorities?.length) {
    newParams.set("filter_priorities", filters.priorities.join(","));
  }

  if (filters.search) {
    newParams.set("filter_search", filters.search);
  }

  if (filters.dueDates) {
    const activeDueDates = Object.entries(filters.dueDates)
      .filter(([_, value]) => value === true)
      .map(([key, _]) => key);

    if (activeDueDates.length > 0) {
      newParams.set("filter_dueDates", activeDueDates.join(","));
    }
  }

  if (filters.conditions) {
    const nonDefaultConditions: Record<string, string> = {};
    let hasNonDefault = false;

    Object.entries(filters.conditions).forEach(([key, value]) => {
      if (!isDefaultCondition(value)) {
        nonDefaultConditions[key] = value;
        hasNonDefault = true;
      }
    });

    if (hasNonDefault) {
      newParams.set("filter_conditions", JSON.stringify(nonDefaultConditions));
    }
  }

  return newParams;
};

export const deserializeFiltersFromParams = (
  params: URLSearchParams,
): Partial<FilterState> | null => {
  const filters: Partial<FilterState> = {};
  let hasAnyFilter = false;

  const userIds = params.get("filter_userIds");
  if (userIds) {
    filters.userIds = userIds.split(",").filter(Boolean);
    hasAnyFilter = true;
  }

  const labels = params.get("filter_labels");
  if (labels) {
    filters.labels = labels.split(",").filter(Boolean);
    hasAnyFilter = true;
  }

  const statuses = params.get("filter_statuses");
  if (statuses) {
    filters.statuses = statuses.split(",").filter(Boolean);
    hasAnyFilter = true;
  }

  const estimates = params.get("filter_estimates");
  if (estimates) {
    filters.estimates = estimates
      .split(",")
      .map(Number)
      .filter((n) => !isNaN(n));
    hasAnyFilter = true;
  }

  const priorities = params.get("filter_priorities");
  if (priorities) {
    filters.priorities = priorities
      .split(",")
      .map(Number)
      .filter((n) => !isNaN(n));
    hasAnyFilter = true;
  }

  const search = params.get("filter_search");
  if (search) {
    filters.search = search;
    hasAnyFilter = true;
  }

  const dueDates = params.get("filter_dueDates");
  if (dueDates) {
    const activeDueDateKeys = dueDates.split(",").filter(Boolean);
    filters.dueDates = {
      none: activeDueDateKeys.includes("none"),
      overdue: activeDueDateKeys.includes("overdue"),
      today: activeDueDateKeys.includes("today"),
      thisWeek: activeDueDateKeys.includes("thisWeek"),
      nextWeek: activeDueDateKeys.includes("nextWeek"),
      later: activeDueDateKeys.includes("later"),
    };
    hasAnyFilter = true;
  }

  const conditions = params.get("filter_conditions");
  if (conditions) {
    try {
      filters.conditions = JSON.parse(conditions);
      hasAnyFilter = true;
    } catch (error) {
      console.warn("Failed to parse filter conditions from URL:", error);
    }
  }

  return hasAnyFilter ? filters : null;
};
